<script setup lang="ts">
import { Loader2 } from "@lucide/vue";
import { useI18n } from "vue-i18n";
import { RouterView, useRoute, useRouter } from "vue-router";

import { useSessionStore } from "@/entities/session";
import { safeReturnPath } from "@/shared/lib";
import { FullPageError } from "@/shared/ui";

const { t } = useI18n();
const session = useSessionStore();
const router = useRouter();
const route = useRoute();

/**
 * Retry boot-error (ADR-0006 D2, Amendment A1, spec §7/§9.1).
 * Amendment A1: main.ts більше НЕ awaits bootstrap() перед mount — SPA
 * монтується оптимістично (D2′: hydrateOptimistic → mount → фонова revalidate).
 * cold-path bootstrap виконується у фоні через whenBootstrapped() (guard).
 *
 * onBootRetry викликається коли status==='error' (cold-path збій мережі/5xx).
 * Після успішного retry замінюємо route на intended — інакше залогінений
 * лишиться на FullPageError із return-path у query:
 * - `authenticated` → `router.replace` у safe return-path;
 * - `error` (повторний збій) → лишаємось на FullPageError (status===error);
 * - `anonymous` (401) → НЕ навігуємо, guard коректно тримає /login (справді анонім).
 */
async function onBootRetry(): Promise<void> {
  await session.bootstrap();
  if (session.isAuthenticated) {
    await router.replace(safeReturnPath(route.query.return));
  }
}
</script>

<template>
  <FullPageError
    v-if="session.status === 'error'"
    :title="t('errors.boot.title')"
    :description="t('errors.boot.description')"
    retriable
    :retry-label="t('errors.boot.retry')"
    :on-retry="onBootRetry"
  />
  <div
    v-else-if="session.status === 'pending'"
    class="min-h-dvh grid place-items-center bg-background"
    role="status"
    :aria-label="t('errors.boot.loading')"
  >
    <Loader2
      class="size-10 animate-spin text-muted-foreground"
      aria-hidden="true"
    />
  </div>
  <RouterView v-else />
</template>
